// Segmento comercial a partir do CNAE — ponte entre a Lista Fria e a Prospecção Geográfica.
//
// Por que existe: as duas bases falam línguas diferentes. A Prospecção Geográfica filtra
// pelos segmentos do Google Places ("Mercado / Supermercado", "Padaria / Confeitaria"),
// mas na Lista Fria a coluna `categoria` guarda a NATUREZA JURÍDICA que veio do CNPJ BIZ
// ("Sociedade Empresária Limitada", "Empresário (Individual)"). Medido em 13/08/2026:
// 11.340 das 11.341 linhas de upload têm natureza jurídica ali. Como o filtro comparava
// string exata, cruzar cidade + segmento devolvia SEMPRE zero.
//
// A tradução sai do CNAE (atividade principal), que a Lemit grava em
// `enriquecimento_data.cnae`. O código é a chave boa: é estável e não depende de acento,
// caixa ou de como a descrição foi abreviada. A descrição entra só como desempate para
// as linhas cujo código não veio.

/** Prefixo do CNAE (só dígitos) → segmento da Prospecção Geográfica. Mais longo vence. */
const POR_CODIGO: Array<[string, string]> = [
  // Varejo & alimentação
  ['4711', 'Mercado / Supermercado'],
  ['4712', 'Mercado / Supermercado'],
  // 4729-6/99 = "produtos alimentícios em geral" — a mercearia de bairro cai aqui
  ['4729', 'Mercado / Supermercado'],
  ['4722', 'Açougue'],
  ['47212', 'Padaria / Confeitaria'],
  ['4721', 'Padaria / Confeitaria'],
  ['4723', 'Distribuidora de Bebidas'],
  ['4724', 'Hortifruti / Verdurão'],
  ['4731', 'Posto de Combustível'],
  ['4771', 'Farmácia / Drogaria'],
  ['4772', 'Farmácia / Drogaria'],
  ['5611', 'Restaurante / Lanchonete'],
  ['5612', 'Restaurante / Lanchonete'],
  ['4635', 'Atacadista / Distribuidor'],
  ['4639', 'Atacadista / Distribuidor'],
  ['4691', 'Atacadista / Distribuidor'],
  ['1053', 'Sorveteria'],
  // Saúde
  ['8610', 'Clínica / Hospital'],
  ['8622', 'Clínica / Hospital'],
  ['8630', 'Clínica Médica'],
  // 8640 se divide: laboratório, diálise e imagem são categorias SEPARADAS na tela, e
  // o prefixo curto jogava tudo em "Laboratório de Análises". Prefixo maior vence.
  ['8640', 'Laboratório de Análises'],
  ['8640203', 'Centro de Diálise'],
  ['8640204', 'Clínica de Imagem'],   // tomografia
  ['8640205', 'Clínica de Imagem'],   // imagem com radiação ionizante
  ['8640206', 'Clínica de Imagem'],   // ressonância magnética
  ['8640207', 'Clínica de Imagem'],   // imagem sem radiação ionizante
  ['8640209', 'Clínica de Imagem'],   // endoscopia / métodos ópticos
  // Educação & hospedagem
  ['5510', 'Hotel / Pousada'],
  ['5590', 'Hotel / Pousada'],
  // 5510-8/03 = MOTÉIS: a tela tem categoria própria e o prefixo 5510 a engolia como hotel
  ['5510803', 'Motel'],
  ['8511', 'Escola / Colégio'],
  ['8512', 'Escola / Colégio'],
  ['8513', 'Escola / Colégio'],
  ['8520', 'Escola / Colégio'],
  ['8531', 'Faculdade / Universidade'],
  ['8532', 'Faculdade / Universidade'],
  // Serviços
  ['6920', 'Escritório de Contabilidade'],
  ['8112', 'Condomínio'],
  // Agro
  ['4623106', 'Armazém de Sementes'],
  ['0151', 'Produtor Rural'],
  ['0152', 'Produtor Rural'],
  ['0155', 'Produtor Rural'],
  ['1052', 'Laticínio'],
  // Academia
  ['9313', 'Academia / Esporte'],
]

/** Palavra-chave na descrição → segmento. Só entra quando o código não resolve. */
const POR_DESCRICAO: Array<[RegExp, string]> = [
  [/SUPERMERCAD|HIPERMERCAD|MINIMERCAD|MERCEARIA|PREDOMINANCIA DE PRODUTOS ALIMENT/, 'Mercado / Supermercado'],
  [/ACOUGUE|VAREJISTA DE CARNES/, 'Açougue'],
  [/PADARIA|CONFEITARIA|PANIFICA/, 'Padaria / Confeitaria'],
  [/SORVETE/, 'Sorveteria'],
  [/LANCHONETE|RESTAURANTE|CASAS DE CHA|SUCOS E SIMILARES/, 'Restaurante / Lanchonete'],
  [/BOTEQUIM|SERVE BEBIDAS|BARES E OUTROS/, 'Bar / Botequim'],
  [/HORTIFRUTI|VERDUR/, 'Hortifruti / Verdurão'],
  [/BEBIDAS/, 'Distribuidora de Bebidas'],
  [/COMBUSTIVE/, 'Posto de Combustível'],
  [/FARMACEUTIC|DROGARIA|FARMACIA/, 'Farmácia / Drogaria'],
  [/ATACADISTA|ATACADO/, 'Atacadista / Distribuidor'],
  [/HOTE|POUSADA|MOTEL|ALOJAMENTO/, 'Hotel / Pousada'],
  [/HOSPITAL/, 'Clínica / Hospital'],
  [/ODONTOLOGIC|MEDICA AMBULATORIAL|PSICOLOGIA|FISIOTERAPIA|CLINICA/, 'Clínica Médica'],
  [/LABORATORIO|ANALISES CLINICAS|DIAGNOSTICO POR IMAGEM/, 'Laboratório de Análises'],
  [/ENSINO SUPERIOR|UNIVERSIT/, 'Faculdade / Universidade'],
  [/ENSINO (FUNDAMENTAL|MEDIO|PRE|INFANTIL)|CRECHE|ESCOLA/, 'Escola / Colégio'],
  [/ACADEMIA|CONDICIONAMENTO FISICO/, 'Academia / Esporte'],
  [/LEITE|LATICINIO/, 'Laticínio'],
  [/CULTIVO|CRIACAO DE BOVIN|AGRICULTURA|PECUARIA/, 'Produtor Rural'],
]

/** Tira acentos e caixa para a descrição casar independente de como veio grafada. */
const semAcento = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()

export interface CnaeLike {
  numero?: string | null
  segmento?: string | null
  descricao?: string | null
}

/**
 * Traduz o CNAE para um segmento da Prospecção Geográfica.
 * Devolve `null` quando não dá para afirmar — melhor não classificar do que classificar
 * errado e mandar um vendedor numa porta que não é do ramo que ele filtrou.
 */
export function segmentoDoCnae(cnae: CnaeLike | null | undefined): string | null {
  if (!cnae) return null

  // `segmento` costuma repetir o código; `numero` é a fonte canônica.
  const codigo = String(cnae.numero || cnae.segmento || '').replace(/\D/g, '')
  if (codigo) {
    let melhor: string | null = null
    let melhorLen = 0
    for (const [pref, seg] of POR_CODIGO) {
      if (codigo.startsWith(pref) && pref.length > melhorLen) { melhor = seg; melhorLen = pref.length }
    }
    if (melhor) return melhor
  }

  const desc = semAcento(String(cnae.descricao || ''))
  if (desc) {
    for (const [re, seg] of POR_DESCRICAO) {
      if (re.test(desc)) return seg
    }
  }
  return null
}

/** Naturezas jurídicas que o CNPJ BIZ grava em `categoria` — não são segmento de verdade. */
const NAO_E_SEGMENTO = new Set([
  'sociedade empresaria limitada',
  'empresario (individual)',
  'sociedade simples limitada',
  'sociedade simples pura',
  'sociedade unipessoal de advocacia',
  'empresa simples de inovacao',
  'sociedade anonima fechada',
  'sociedade anonima aberta',
  'lista fria',
  'adicionado manualmente',
])

/** A `categoria` gravada na linha é um segmento comercial ou só a natureza jurídica? */
export const categoriaEhSegmento = (categoria?: string | null): boolean => {
  const c = semAcento(String(categoria || '')).toLowerCase().trim()
  return !!c && !NAO_E_SEGMENTO.has(c)
}

/**
 * Segmento efetivo de uma linha da Lista Fria: a `categoria` quando ela já é um segmento
 * de verdade, senão a tradução do CNAE. `null` = não classificada.
 */
export function segmentoDaLinha(
  linha: { categoria?: string | null; enriquecimento_data?: { cnae?: CnaeLike | null } | null },
): string | null {
  if (categoriaEhSegmento(linha.categoria)) return String(linha.categoria)
  return segmentoDoCnae(linha.enriquecimento_data?.cnae)
}
